import moment from "moment";
import { DATE_FORMAT, FAIL_THRESHOLD } from "./constants";

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Nullable foreign keys must be null, never "" or 0.
 * @param {unknown} value
 * @returns {number|null}
 */
export const toIdOrNull = (value) => {
  const n = toNumberOrNull(value);
  return n && n > 0 ? n : null;
};

/**
 * @param {unknown} value  moment, Date or date string
 * @returns {string|null}
 */
export const toDateOrNull = (value) => {
  if (!value) return null;
  const m = moment.isMoment(value) ? value : moment(value);
  return m.isValid() ? m.format(DATE_FORMAT) : null;
};

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * @param {import("./types").DefectCounts} defectCounts
 * @param {string|number} subcategoryId
 * @returns {number}
 */
export const countFor = (defectCounts, subcategoryId) => {
  const n = toNumberOrNull(defectCounts?.[subcategoryId]);
  return n && n > 0 ? n : 0;
};

/**
 * penalty point = (total defects * 3600) / (actual length * actual width)
 * @param {number} count
 * @param {unknown} actualLength
 * @param {unknown} actualWidth
 * @returns {number}
 */
export const calculatePenaltyPoint = (count, actualLength, actualWidth) => {
  const area = (toNumberOrNull(actualLength) || 0) * (toNumberOrNull(actualWidth) || 0);
  if (!area || !count) return 0;
  return round2((count * 3600) / area);
};

/**
 * @param {number} penaltyPoint
 * @returns {"Pass"|"Fail"}
 */
export const statusFor = (penaltyPoint) => (penaltyPoint > FAIL_THRESHOLD ? "Fail" : "Pass");

/**
 * Extract integer point multiplier from subcategory name.
 * e.g., "1" -> 1, "2" -> 2, "3" -> 3, "4" -> 4, "2 Points" -> 2
 * @param {unknown} name
 * @returns {number}
 */
export const getSubcategoryMultiplier = (name) => {
  if (name === null || name === undefined || name === "") return 1;
  const match = String(name).match(/\d+/);
  if (!match) return 1;
  const parsed = parseInt(match[0], 10);
  return Number.isFinite(parsed) ? parsed : 1;
};

/**
 * Normalizes categories, subcategories or existing details into a lookup map:
 * { [subcategoryId]: DefectSubcategory }
 * @param {unknown} categoriesOrSubcategories
 * @param {Array} [fallbackDetails]
 * @returns {Record<string, any>}
 */
export const buildSubcategoryMap = (categoriesOrSubcategories, fallbackDetails = []) => {
  const map = {};
  if (Array.isArray(categoriesOrSubcategories)) {
    for (const item of categoriesOrSubcategories) {
      if (item && Array.isArray(item.subcategories)) {
        for (const sub of item.subcategories) {
          if (sub && sub.id != null) map[String(sub.id)] = sub;
        }
      } else if (item && item.id != null) {
        map[String(item.id)] = item;
      }
    }
  } else if (categoriesOrSubcategories && typeof categoriesOrSubcategories === "object") {
    Object.assign(map, categoriesOrSubcategories);
  }

  if (Array.isArray(fallbackDetails)) {
    for (const d of fallbackDetails) {
      const sub = d?.defect_subcategory || d?.defectSubcategory;
      const subId = d?.defect_subcategory_id || sub?.id;
      if (subId && sub && !map[String(subId)]) {
        map[String(subId)] = sub;
      }
    }
  }

  return map;
};

/**
 * Live summary shown in the modal and report tables.
 * Total Defect = SUM(integer of defectSubcategory.name * defect_value)
 * penalty point = (totalDefect * 3600) / (actualLength * actualWidth)
 * status = penaltyPoint > FAIL_THRESHOLD ? "Fail" : "Pass"
 *
 * @param {import("./types").DefectCounts} defectCounts
 * @param {unknown} actualLength
 * @param {unknown} actualWidth
 * @param {unknown} [categoriesOrSubcategories]
 * @param {Array} [fallbackDetails]
 * @returns {{totalDefect:number, penaltyPoint:number, status:"Pass"|"Fail"}}
 */
export const summarizeDefects = (
  defectCounts,
  actualLength,
  actualWidth,
  categoriesOrSubcategories,
  fallbackDetails = [],
) => {
  const subMap = buildSubcategoryMap(categoriesOrSubcategories, fallbackDetails);
  const totalDefect = Object.keys(defectCounts || {}).reduce((sum, id) => {
    const count = countFor(defectCounts, id);
    if (!count) return sum;
    const sub = subMap[String(id)];
    const multiplier = getSubcategoryMultiplier(sub?.name);
    return sum + multiplier * count;
  }, 0);
  const penaltyPoint = calculatePenaltyPoint(totalDefect, actualLength, actualWidth);
  return { totalDefect, penaltyPoint, status: statusFor(penaltyPoint) };
};

/**
 * Builds detail rows with total_defect_value = (integer of defectSubcategory.name) * defect_value.
 * penalty_point = total_defect_value * 3600 / (actualLength * actualWidth). Every detail carries the
 * inspection's overall status (Fail when total penalty point > FAIL_THRESHOLD).
 *
 * FabricInspectionRequest has no rule for is_delete/is_deleted, so validated() strips both
 * and the backend updates the row instead. Removed defects are therefore also zeroed.
 *
 * @param {import("./types").DefectCounts} defectCounts
 * @param {import("./types").InspectionDetail[]} existingDetails
 * @param {unknown} actualLength
 * @param {unknown} actualWidth
 * @param {"create"|"edit"} mode
 * @param {unknown} [categoriesOrSubcategories]
 * @returns {import("./types").InspectionDetailPayload[]}
 */
export const buildInspectionDetails = (
  defectCounts,
  existingDetails,
  actualLength,
  actualWidth,
  mode,
  categoriesOrSubcategories,
) => {
  const existing = mode === "edit" ? existingDetails || [] : [];
  const { status } = summarizeDefects(
    defectCounts,
    actualLength,
    actualWidth,
    categoriesOrSubcategories,
    existing,
  );
  const subMap = buildSubcategoryMap(categoriesOrSubcategories, existing);
  const usedExistingIds = new Set();

  const kept = Object.keys(defectCounts || {})
    .filter((subId) => countFor(defectCounts, subId) > 0)
    .map((subId) => {
      const count = countFor(defectCounts, subId);
      const sub = subMap[String(subId)];
      const multiplier = getSubcategoryMultiplier(sub?.name);
      const totalDefectValue = multiplier * count;
      const match = existing.find(
        (d) => Number(d.defect_subcategory_id) === Number(subId) && !usedExistingIds.has(d.id),
      );
      if (match) usedExistingIds.add(match.id);
      return {
        id: match ? Number(match.id) : 0,
        is_delete: 0,
        is_deleted: 0,
        defect_subcategory_id: Number(subId),
        defect_value: count,
        total_defect_value: totalDefectValue,
        penalty_point: calculatePenaltyPoint(totalDefectValue, actualLength, actualWidth),
        status,
      };
    });

  const removed = existing
    .filter((d) => !usedExistingIds.has(d.id))
    .map((d) => ({
      id: Number(d.id),
      is_delete: 1,
      is_deleted: 1,
      defect_subcategory_id: Number(d.defect_subcategory_id),
      defect_value: 0,
      total_defect_value: 0,
      penalty_point: 0,
      status,
    }));

  return [...kept, ...removed];
};

/**
 * @param {import("./types").InspectionFormState & {defectCategories?: any, categories?: any}} form
 * @param {"create"|"edit"} mode
 * @returns {import("./types").InspectionPayload}
 */
export const buildInspectionPayload = (form, mode) => {
  const style = form.style;
  const item = form.inventoryItem;
  const inventory = item?.inventory;
  const savedItem = form.savedItem;
  const savedReceived = form.savedReceived;

  return {
    style_id: toIdOrNull(style?.id),
    sales_contract_id: toIdOrNull(style?.active_sales_contract_map_info?.sales_contract_id),
    buying_office_id: toIdOrNull(style?.buying_office_id),
    buyer_id: toIdOrNull(style?.buyer_id),
    sub_contract_factory_id: toIdOrNull(style?.sub_contract_factory_id),
    supplier_id: toIdOrNull(form.supplierId),
    item_category_id: toIdOrNull(item ? item.item_category_id : savedItem?.item_category_id),
    item_id: toIdOrNull(item ? item.item_id : savedItem?.item_id),
    color_id: toIdOrNull(form.colorId),
    sub_style_id: toIdOrNull(item ? item.sub_style_id : savedItem?.sub_style_id),
    inspection_date: toDateOrNull(form.inspectionDate),
    received_date: toDateOrNull(item ? inventory?.receive_date : savedReceived?.received_date),
    received_qty: toNumberOrNull(item ? inventory?.rcv_qty : savedReceived?.received_qty),
    received_roll: toNumberOrNull(item ? inventory?.roll_qty : savedReceived?.received_roll),
    inspected_roll: toNumberOrNull(form.inspectedRoll),
    tag_length: toNumberOrNull(form.tagLength),
    tag_actual_length: toNumberOrNull(form.tagActualLength),
    tag_width: toNumberOrNull(form.tagWidth),
    tag_actual_width: toNumberOrNull(form.tagActualWidth),
    note: form.note || "",
    // inventory_detail_id is omitted: the inventory endpoint returns a BOM detail id, which would
    // mark an unrelated inventory_details row as inspected.
    inspection_details: buildInspectionDetails(
      form.defectCounts,
      form.existingDetails,
      form.tagActualLength,
      form.tagActualWidth,
      mode,
      form.defectCategories || form.categories,
    ),
  };
};
